/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

const SettingToggle = defineComponent<{
  icon: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}>({
  name: "SettingToggle",
  props: ["icon", "label", "description", "checked", "onChange"],
  setup(props) {
    return () => (
      <div class="form-control">
        <label class="cursor-pointer">
          <div class="flex items-center justify-between p-4 border-2  rounded-xl hover:border-primary transition-all">
            <div class="flex items-center gap-3">
              <span class="material-symbols-outlined text-2xl text-base-content/70">
                {props.icon}
              </span>
              <div>
                <span class="label-text font-semibold text-base">
                  {props.label}
                </span>
                <p class="text-xs text-base-content/60 mt-0.5">
                  {props.description}
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={props.checked}
              class="toggle toggle-primary"
              onChange={(event: Event) =>
                props.onChange((event.target as HTMLInputElement).checked)
              }
            />
          </div>
        </label>
      </div>
    );
  },
});

export const TableOrderSettingsCard = defineComponent<{
  reduceQuantitiesUpdateKitchenDefault: boolean;
  onUpdateReduceQuantitiesUpdateKitchenDefault: (value: boolean) => void;
  allowOrdersExceedingTableCredit: boolean;
  onUpdateAllowOrdersExceedingTableCredit: (value: boolean) => void;
  giftCardAmounts: number[];
  onUpdateGiftCardAmounts: (value: number[]) => void;
}>({
  name: "TableOrderSettingsCard",
  props: [
    "reduceQuantitiesUpdateKitchenDefault",
    "onUpdateReduceQuantitiesUpdateKitchenDefault",
    "allowOrdersExceedingTableCredit",
    "onUpdateAllowOrdersExceedingTableCredit",
    "giftCardAmounts",
    "onUpdateGiftCardAmounts",
  ],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    // Edited as text like the tip steps (TipsSettingsCard), parsed on blur.
    const amountsInput = ref((props.giftCardAmounts || []).join(", "));
    watch(
      () => props.giftCardAmounts,
      (value) => (amountsInput.value = (value || []).join(", ")),
    );
    const handleAmountsBlur = () => {
      const amounts = amountsInput.value
        .split(",")
        .map((s) => parseFloat(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (amounts.length > 0) {
        props.onUpdateGiftCardAmounts(
          [...new Set(amounts)].sort((a, b) => a - b),
        );
      } else {
        amountsInput.value = (props.giftCardAmounts || []).join(", ");
      }
    };

    return () => (
      <div class="l-box-shadow rounded rounded-2xl shadow-xl bg-base-100 border border-base-100">
        <div class="card-body">
          <div class="flex items-center gap-3 mb-6">
            <div class="p-3 bg-warning/10 rounded-xl">
              <span class="material-symbols-outlined text-warning text-2xl">
                room_service
              </span>
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-accent">
                {t("settings.config.table_orders")}
              </h3>
              <p class="text-sm text-base-content">
                {t("settings.config.table_orders_desc")}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <SettingToggle
              icon="print"
              label={t(
                "settings.config.reduce_quantities_update_kitchen_default",
              )}
              description={t(
                "settings.config.reduce_quantities_update_kitchen_default_desc",
              )}
              checked={props.reduceQuantitiesUpdateKitchenDefault}
              onChange={props.onUpdateReduceQuantitiesUpdateKitchenDefault}
            />
            <SettingToggle
              icon="account_balance_wallet"
              label={t("settings.config.allow_orders_exceeding_table_credit")}
              description={t(
                "settings.config.allow_orders_exceeding_table_credit_desc",
              )}
              checked={props.allowOrdersExceedingTableCredit}
              onChange={props.onUpdateAllowOrdersExceedingTableCredit}
            />
            <div class="flex items-start gap-3 p-4 border-2 rounded-xl">
              <span class="material-symbols-outlined text-2xl text-base-content/70">
                redeem
              </span>
              <label class="form-control flex-1 gap-1">
                <span class="label-text font-semibold text-base">
                  {t("settings.config.gift_card_amounts")} (
                  {auth.user.value?.restaurant.currency || "CHF"})
                </span>
                <span class="text-xs text-base-content/60">
                  {t("settings.config.gift_card_amounts_desc")}
                </span>
                <input
                  type="text"
                  inputmode="decimal"
                  class="input input-bordered input-sm md:input-md mt-1"
                  placeholder="50, 100, 200, 500, 1000"
                  value={amountsInput.value}
                  onInput={(e: Event) => {
                    amountsInput.value = (e.target as HTMLInputElement).value;
                  }}
                  onBlur={handleAmountsBlur}
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
